import {
  Component, createNgModule,
  CUSTOM_ELEMENTS_SCHEMA, forwardRef, inject,
  Injectable, Injector,
  NgModule,
  NO_ERRORS_SCHEMA,
  ViewEncapsulation
} from '@angular/core';

import { extendProto, isDefined, runtimeEventManager } from '@wm/core';
import { transpile } from '@wm/transpiler';
import { initComponentsBuildTask } from '@wm/build-task';

import {
  NativeMobileModule,
  RuntimeBaseModule,
} from "@wm/design-runtime";
import {
    Parent,
} from "@wm/components-base";
import {AppRef} from "./app.service";
import {getCanvasElement, MarkupManagerService} from "@wms/core";
import { get, extend, isEmpty } from "lodash-es";
import {PIPES, RN_SPECIFIC_STANDALONE_COMPONENTS, STANDALONE_COMPONENTS} from "@wms/design-container";
import { ProjectService } from "@wms/services";

initComponentsBuildTask();

const componentFactoryRefCache = new Map<string, any>();

const getDynamicModule = (componentRef: any, isNativeApp: boolean) => {
  let imports = [
    RuntimeBaseModule,
    ...STANDALONE_COMPONENTS,
    ...PIPES,
    ...(isNativeApp ? RN_SPECIFIC_STANDALONE_COMPONENTS : [])
  ];

    return NgModule({
        declarations: [
            componentRef
        ],
        imports: imports,
        providers: [
            { provide: Parent, useExisting: forwardRef(() => componentRef) },
            ...PIPES
        ],
        schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
    })(
      class DynamicModule {}
    )
};
const getDynamicComponent = (
    selector: any,
    template: string,
    css: string = '') => {
  // @ts-ignore
  const component = Component({
      template,
      styles: [css],
      encapsulation: ViewEncapsulation.None,
        selector,
      providers: [
        { provide: Parent, useExisting: forwardRef(() => component) }
      ]
    })(
    class DynamicComponent {
      onPageContentReady() {}
    })
  return component;
};

@Injectable()
export class DynamicComponentRefProviderService {
    private counter = 1;
    private isNativeApp: boolean;
    private projectService: ProjectService;

    constructor(
        private app: AppRef,
        private injector: Injector,
        private markupManager: MarkupManagerService
    ) {
      this.projectService = inject(ProjectService);
      runtimeEventManager.setService('dynamicComponentRefProvider', this);
      this.isNativeApp = this.projectService.isNativeMobileApplicationType();
    }

    public async getComponentFactoryRef(selector: string, markup: string, options: any = {}): Promise<any> {
        // check in the cache.
        let componentFactoryRef = componentFactoryRefCache.get(selector);

        markup = (options.transpile ? transpile(markup)?.markup : markup) || '';
        if (!componentFactoryRef || options.noCache) {
            const componentDef = getDynamicComponent(selector, markup, options.styles);
            const moduleDef = getDynamicModule(componentDef, this.isNativeApp);
            let containerRef = options.viewContainerRef || this.app.dynamicComponentContainerRef;

            componentFactoryRef = containerRef.createComponent(componentDef, {
              injector: this.injector,
              ngModuleRef: createNgModule(moduleDef, this.injector),
            })
            componentFactoryRefCache.set(selector, componentFactoryRef);
        }
        return componentFactoryRef;
    }

    /**
     * Creates the component dynamically and add it to the DOM
     * @param target: HTML element where we need to append the created component
     * @param markup: Template of the component
     * @param context: Scope of the component
     * @param options: We have options like
                       selector: selector of the component
                       transpile: flag for transpiling HTML. By default it is true
                       nocache: flag for render it from cache or not. By default it is true
     */
    public async addComponent(target: HTMLElement, markup: string, context: any, options: any = {}) {
        options.transpile = isDefined(options.transpile) ? options.transpile : true;
        options.noCache = isDefined(options.noCache) ? options.noCache : true;
        options.selector = isDefined(options.selector) ? options.selector : 'wm-dynamic-component-' + this.counter++;
        const component = await this.getComponentFactoryRef(options.selector, markup, options);
      if (isEmpty(context)) {
        context = this.getContext();
      }
        extend(component.instance, context);
        if (options.replace) {
          let addedEl = component.location.nativeElement;
          // let compiledEl = $(addedEl).find("[widgetid='" + target.getAttribute('widgetid') + "']");
          // removing the dynamic component wrapper added upon the element
          if (addedEl.childNodes.length >= 1) {
            addedEl = addedEl.childNodes[0];
          }
          // $(target).append(compiledEl.children());
          // target.innerHTML = compiledEl[0].innerHTML
        } else {
          target.appendChild(component.location.nativeElement);
        }
    }

  public async addComponentBasedOnSelector(target: HTMLElement | null, markup: string, context: any, options: any = {}) {
    options.transpile = isDefined(options.transpile) ? options.transpile : true;
    options.noCache = isDefined(options.noCache) ? options.noCache : true;
    options.selector = isDefined(options.selector) ? options.selector : 'wm-dynamic-component-' + this.counter++;
    const componentFactoryRef = await this.getComponentFactoryRef(options.selector, markup, options);
    if (isEmpty(context)) {
      context = this.getContext();
    }
    // const component = this.app.dynamicComponentContainerRef.createComponent(componentFactoryRef, 0, get(options, 'inj'));
    extend(componentFactoryRef.instance, context);
    extendProto(componentFactoryRef.instance, context);
    let addedEl = componentFactoryRef.location.nativeElement;
    // removing the dynamic component wrapper added upon the element
    if (addedEl.childNodes.length >= 1) {
      addedEl = addedEl.childNodes[0];
    }

    if (target) {
      if (options.replaceChildren) {
        // @ts-ignore
        target.append($(addedEl).children());
      } else if (options.addAfter) {
        target.after(addedEl);
      } else {
        target.before(addedEl);
      }
    } else if (options.parentEl) {
      let parentEl = options.parentEl;
      let hasTransclude = parentEl.attr('wmstransclude');
      if (hasTransclude === "") {
        parentEl.append(addedEl);
      } else {
        // hack for liveform
        // live-from will not have wmtransclude because live form fields needs to be compiled with the isolateScope of live form
        // add the elements to the form-elements node of the live-form
        if (parentEl.attr('widgettype') === 'wm-liveform') {
          parentEl.children('.form-elements.panel-body').append(addedEl);
        } else {
          let dropTarget = parentEl.find('[data-droptarget-for=' + parentEl.attr('widget-id') + ']');
          if (dropTarget.length) {
            dropTarget.first().append(addedEl);
          } else if (parentEl.find('[wmstransclude]')?.length) {
            parentEl.find('[wmstransclude]').first().append(addedEl);
          }
        }
      }
    }
    // Removes the extra wm-dynamic-node added to the html.
    componentFactoryRef.location.nativeElement.remove();
    componentFactoryRef.changeDetectorRef.detectChanges();
    return addedEl;
  }

  getContext() {
    let markup = this.markupManager.getMarkup();
    let pageRootNode = markup && markup[0].firstChild;

    if (get(pageRootNode, 'getAttribute')) {
      let parentId = (pageRootNode as HTMLElement).getAttribute('widgetid') as string;
      let shadowHost = document.querySelector('wms-canvas-sandbox-wrapper');
      let $canvasEl: any;

      /* If project is of type prism then it'll be wrapped inside the shadow dom and any selector inside it
      needs to be accessed via the same */
      $canvasEl = $(getCanvasElement());

      let parentNode = $canvasEl.find('[widget-id="' + parentId +'"]')[0];

      return get(parentNode, 'widget.viewParent');
    }

    return {};
  }
}
